import { z } from 'zod';
import { supportedLanguages } from './languages';
import { ConsentType } from '../enums';
import { emailSchema, passwordSchema } from './common';

/**
 * §4: при регистрации пользователь принимает пять документов.
 * Принимаются конкретные версии — если документ обновился между загрузкой формы
 * и отправкой, регистрация отклоняется, а не сохраняет устаревшее согласие.
 */
export const acceptedConsentsSchema = z.record(
  z.enum(Object.values(ConsentType) as [string, ...string[]]),
  z.string().min(1),
);

export const registerSchema = z
  .object({
    /**
     * Язык, выбранный до регистрации.
     *
     * Нужен здесь, а не только в настройках: письмо с подтверждением уходит
     * в тот же миг, раньше любого следующего запроса.
     */
    locale: z.enum(supportedLanguages as [string, ...string[]]).optional(),
    email: emailSchema,
    password: passwordSchema,
    name: z.string().trim().min(1).max(80).optional(),
    /** Код из ?ref= — атрибуция реферала (§40.3), начисления в фазе 13. */
    referralCode: z.string().trim().max(32).optional(),
    acceptedConsents: acceptedConsentsSchema,
  })
  .strict();
export type RegisterInput = z.infer<typeof registerSchema>;

export const loginSchema = z
  .object({
    email: emailSchema,
    password: z.string().min(1, 'Введите пароль'),
  })
  .strict();
export type LoginInput = z.infer<typeof loginSchema>;

export const verifyEmailSchema = z.object({ token: z.string().min(16) }).strict();

export const resendVerificationSchema = z.object({ email: emailSchema }).strict();

export const forgotPasswordSchema = z.object({ email: emailSchema }).strict();

export const resetPasswordSchema = z
  .object({
    token: z.string().min(16),
    password: passwordSchema,
  })
  .strict();

/** Ответ на login / refresh. Refresh-токен сюда не попадает — он в httpOnly-cookie. */
export interface AuthSession {
  accessToken: string;
  expiresIn: number;
  user: AuthUser;
}

export interface AuthUser {
  id: string;
  email: string;
  name: string | null;
  role: 'user' | 'admin';
  /**
   * Служебный аккаунт (§41).
   *
   * Видит техническую изнанку своих роликов — какими сервисами и моделями
   * сделана каждая сцена. Обычный пользователь не видит этого нигде, и это
   * критическое требование; отметку ставит админ поимённо.
   */
  isService: boolean;
  emailVerified: boolean;
  locale: string;
  createdAt: string;
  /** Список типов согласий, которые нужно принять заново (документ обновился). */
  pendingConsents: string[];
}
